<template>
  <Loader :visible="isLoggingIn" :text="t('login.loading')"></Loader>
</template>

<script setup lang="ts">
import axios from 'axios'
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserProfileStore } from '../../stores/userProfileStore'
import { UserProfileData } from '../../types/userProfile/userProfileData'
import { OauthOption } from '../../types/auth/oauthOption'
import { normalizeAuthData } from '../../composables/auth/normalizeAuthData'
import { t } from '../../composables/Translation'
import { useChatStore } from '../../stores/chatStore'
import { useAppStore } from '../../stores/appStore'
import { useAidaSessionStore } from '../../stores/aidaSessionStore'
import Loader from '../Loader.vue'
import { useToast } from '../../composables/useToast'
import { clearAutoLoginAttempt, setLoginError } from '../../composables/auth/autoLoginGuard'
import { State } from '../../types/app/state'

const backendAuthURL = `${import.meta.env.VITE_BACKEND_BASE_URL}/oauth/callback/`
const router = useRouter()
const toast = useToast()

const userProfileStore = useUserProfileStore()
const chatStore = useChatStore()
const appStore = useAppStore()
const aidaSessionStore = useAidaSessionStore()
const isLoggingIn = ref(true)

onMounted(async () => {
  chatStore.restoreAfterRedirect()
  appStore.restoreAfterRedirect()
  aidaSessionStore.restoreAfterRedirect()
  const parameters = window.location.search.slice(1)
  const oauthProvider = appStore.oauthProvider ?? OauthOption.Okta
  const url = backendAuthURL + oauthProvider.toLowerCase() + '?' + parameters

  const headers = { headers: { 'Content-Type': 'application/json' } }
  try {
    isLoggingIn.value = true
    const response = await axios.get(url, headers)
    if (response?.data) {
      const data = response.data
      const normalized = normalizeAuthData(data, oauthProvider)
      const userProfile = new UserProfileData(normalized, oauthProvider)
      userProfileStore.setUserProfile(userProfile)
      isLoggingIn.value = false
      clearAutoLoginAttempt()
      if (appStore.currentState !== State.AttemptToOpenSR) appStore.setState(State.Main)
      router.push({ name: 'main' })
    }
  } catch (err: any) {
    toast.error(t('login.genericError'))
    const { message, status } = extractBackendError(err)
    console.error(
      `Error during sign in. Message: ${message} Status: ${status} Provider: ${oauthProvider}`
    )
    setLoginError(message, status, oauthProvider)
    if (appStore.currentState !== State.AttemptToOpenSR) appStore.setState(State.Main)
    router.push({ name: 'main' })
  } finally {
    isLoggingIn.value = false
  }
})

function extractBackendError(err: unknown): { message: string; status: string } {
  if (!axios.isAxiosError(err)) {
    return { message: 'Unexpected error', status: '' }
  }

  const status = err.response?.status
  const data = err.response?.data as any

  const message =
    (typeof data?.detail === 'string' && data.detail) ||
    (typeof data?.message === 'string' && data.message) ||
    (typeof data?.error_description === 'string' && data.error_description) ||
    (typeof data?.error === 'string' && data.error) ||
    err.message ||
    'Login failed'

  return {
    message: message.trim(),
    status: status != null ? String(status) : '',
  }
}
</script>
